import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { customerViewConnectionSchema, stageBrandingSchema, type CustomerFilter, type CustomerObject, type CustomerSafeTicket, type CustomerSafeTicketDetail, type CustomerViewConnection, type StageBranding } from "@msc/shared";

import { ApiError, api } from "../lib/api.js";

export const customerViewKeys = {
  connection: ["customerView", "connection"] as const,
  customers: (filter: CustomerFilter) => ["customerView", "customers", filter] as const,
  tickets: (objectKey: string) => ["customerView", "tickets", objectKey] as const,
  /** Präfix ohne Schlüssel: Die Kundenakte invalidiert damit JEDE Kundenabteilung, sie kennt deren Schlüssel nicht. */
  brandingAll: ["customerView", "branding"] as const,
  branding: (objectKey: string) => ["customerView", "branding", objectKey] as const,
  detail: (key: string) => ["customerView", "detail", key] as const,
  actions: (key: string) => ["customerView", "actions", key] as const,
  forms: (key: string) => ["customerView", "forms", key] as const,
  assignable: (key: string, query: string) => ["customerView", "assignable", key, query] as const,
  requestTypes: ["customerView", "requestTypes"] as const,
  requestTypeForm: (id: string) => ["customerView", "requestTypeForm", id] as const,
  contacts: (objectKey: string) => ["customerView", "contacts", objectKey] as const,
};

/**
 * Query-Hooks der Customer View. Scheibe 1: nur der Verbindungsstatus. Die
 * Navigation fragt ihn ebenfalls ab — deshalb ein längeres `staleTime`, damit
 * nicht jeder Seitenwechsel einen Request auslöst.
 */
export function useCustomerViewConnection() {
  return useQuery<CustomerViewConnection>({
    queryKey: customerViewKeys.connection,
    // Geparst statt nur getypt: Das Schema setzt die Vorgaben der optionalen
    // Felder (`site`, `siteProblem`, `siteProblemCode`, `configuredHost`), die
    // eine ältere API nicht mitschickt — sonst wären sie `undefined`, und die
    // Oberfläche entschiede über einen Wert, den sie nie bekommen hat.
    queryFn: async () => customerViewConnectionSchema.parse(await api.get<unknown>("/customer-view/connection")),
    staleTime: 60 * 1000,
  });
}

export function useDisconnectJira() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>("/customer-view/connect"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["customerView"] }),
  });
}

/** 409 aus der API: Der Lead muss Jira neu verbinden (Token fehlt, widerrufen, Scope). */
export function isJiraReauthRequired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409 && err.code === "JIRA_REAUTH_REQUIRED";
}

export function useCustomers(filter: CustomerFilter) {
  return useQuery<CustomerObject[]>({
    queryKey: customerViewKeys.customers(filter),
    queryFn: async () =>
      (await api.get<{ customers: CustomerObject[] }>(`/customer-view/customers?filter=${filter}`)).customers,
    staleTime: 5 * 60 * 1000,
    retry: (count, err) => !isJiraReauthRequired(err) && count < 1,
  });
}

/** Offene und kürzlich erledigte Tickets einer Kundenabteilung — der Server cacht 60 s je Lead. */
export function useTickets(objectKey: string | null) {
  return useQuery<CustomerSafeTicket[]>({
    queryKey: customerViewKeys.tickets(objectKey ?? ""),
    enabled: objectKey !== null,
    queryFn: async () =>
      (await api.get<{ tickets: CustomerSafeTicket[] }>(`/customer-view/customers/${encodeURIComponent(objectKey!)}/tickets`)).tickets,
    staleTime: 30 * 1000,
    retry: (count, err) => !isJiraReauthRequired(err) && count < 1,
  });
}

/**
 * Antwort von `GET /customer-view/customers/:objectKey/branding` lesen —
 * nackt (`StageBranding | null`) oder in einer Hülle `{ branding }`. Alles,
 * was dem Vertrag nicht entspricht, gilt als „kein Design": Das Kundendesign
 * ist Zierde, es darf den Termin nie aufhalten.
 */
export function parseStageBranding(raw: unknown): StageBranding | null {
  const candidate =
    raw !== null && typeof raw === "object" && "branding" in raw
      ? (raw as { branding: unknown }).branding
      : raw;
  const parsed = stageBrandingSchema.nullable().safeParse(candidate ?? null);
  return parsed.success ? parsed.data : null;
}

/**
 * Das Kundendesign für die Bühne (D10) — aufgelöst wie die Agenda über die
 * Kundenabteilung. Fail-soft: Ein Fehler heißt „ohne Design", nie ein roter
 * Kasten im Cockpit und nie etwas auf der Bühne; deshalb kein Retry.
 */
export function useCustomerBranding(objectKey: string | null) {
  return useQuery<StageBranding | null>({
    queryKey: customerViewKeys.branding(objectKey ?? ""),
    enabled: objectKey !== null && objectKey !== "",
    queryFn: async () =>
      parseStageBranding(
        await api.get<unknown>(`/customer-view/customers/${encodeURIComponent(objectKey!)}/branding`),
      ),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useTicketDetail(key: string | null) {
  return useQuery<CustomerSafeTicketDetail>({
    queryKey: customerViewKeys.detail(key ?? ""),
    enabled: key !== null,
    queryFn: () => api.get<CustomerSafeTicketDetail>(`/customer-view/tickets/${encodeURIComponent(key!)}`),
    staleTime: 30 * 1000,
    retry: (count, err) => !isJiraReauthRequired(err) && count < 1,
  });
}
